import Stack from "@mui/material/Stack";
import type { StaffTask } from "services/types/staff-tasks.type";
import FieldLabel from "../../../drawer/staff-task-card/field-label.component";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import { misc } from "app/themes/colors";
import MentionsSummary from "components/mentions/mentions-summary.component";
import { MENTION_PILL_SX } from "components/mentions/mentions.util";
import WordProcessorHtmlContent from "components/word-processor-field/word-processor-html-content.component";
import {
  TASK_STATUS_CANCELLED,
  TASK_STATUS_COMPLETED,
} from "constants/staff-tasks";
import { format } from "date-fns";
import StaffTaskPatientDisplay from "features/staff-tasks-v2/drawer/staff-task-card/staff-task-patient-display.component";
import { prepareTaskDescriptionHtml } from "features/staff-tasks-v2/staff-task.utils";

type TaskFieldsProps = {
  task: StaffTask;
};

export default function TaskFields({ task }: TaskFieldsProps) {
  const isComplete =
    task.status === TASK_STATUS_COMPLETED ||
    task.status === TASK_STATUS_CANCELLED;

  return (
    <Stack
      width="100%"
      rowGap={4}
      sx={{
        height: "500px",
        overflowY: "auto",
        overflowX: "hidden",
        boxSizing: "border-box",
        ml: -2.5,
      }}
    >
      <Box
        sx={{
          background:
            !isComplete && task.comments?.length
              ? misc.lightLavender
              : undefined,

          pl: 2.5,
          pr: 2,
          pb: 3,
          borderRadius: "8px",
          mb: -2.5,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Stack rowGap={1} width="100%" sx={{ mt: 2 }}>
          <FieldLabel label="Title" />
          <Typography
            variant="body3"
            sx={{ fontSize: "24px", lineHeight: 1.3, color: grey[900] }}
          >
            {task.title}
          </Typography>
        </Stack>

        {task.description && (
          <Stack rowGap={1} width="100%" sx={{ mt: 4 }}>
            <FieldLabel label="Description" />
            <Typography variant="body2">
              <WordProcessorHtmlContent
                html={
                  /<\/?[a-z][\s\S]*>/i.test(task.description)
                    ? prepareTaskDescriptionHtml(task.description)
                    : task.description.replaceAll("\n", "<br />")
                }
                sx={{
                  pt: 0,
                  ...MENTION_PILL_SX
                }}
              />
            </Typography>
            {/* Only when the task was read with its mentions (TEAM_MENTIONS_FEATURE). */}
            <MentionsSummary
              mentions={task.mentions}
              label="Mentioned:"
              testId="staff-task-mentions-summary"
            />
          </Stack>
        )}
      </Box>

      <Stack rowGap={1} width="100%" sx={{ pr: 2, mt: 2, ml: 2.5 }}>
        <FieldLabel label="Assignee" />
        <Stack direction="row" columnGap={1.5} alignItems="center">
          <Avatar
            alt="avatar"
            src={task.assignee?.photo ?? ""}
            sx={{
              border: `solid 1px ${grey[400]}`,
              width: "32px",
              height: "32px",
            }}
            slotProps={{ img: { loading: "lazy" } }}
          />
          <Typography variant="body2" sx={{ color: grey[900] }}>
            {task.assignee?.fullName}
          </Typography>
        </Stack>
      </Stack>

      {Boolean(task.assignees.length) && (
        <Stack rowGap={1} width="100%" sx={{ pr: 2, ml: 2.5 }}>
          <FieldLabel label="Additional Assignees" />
          <Typography variant="body2" sx={{ color: grey[900] }}>
            {task.assignees
              .map((assignee) => assignee?.user?.fullName)
              .join(", ")}
          </Typography>
        </Stack>
      )}

      {task?.patient && (
        <Stack rowGap={1} sx={{ pr: 2, ml: 2.5, width: "fit-content" }}>
          <FieldLabel label="Patient" />
          <StaffTaskPatientDisplay
            isOpenInNewTab
            isComplete={false}
            variant="body2"
            patient={task?.patient}
          />
        </Stack>
      )}

      {task?.created_by && (
        <Stack rowGap={1} width="100%" sx={{ pr: 2, ml: 2.5 }}>
          <FieldLabel label="Created by" />
          <Typography variant="body2" sx={{ color: grey[800] }}>
            {task?.created_by?.fullName}
            {task.created_at
              ? `, ${format(
                  new Date(task.created_at),
                  "MMM dd, yyyy hh:mm aaa"
                )}`
              : ""}
          </Typography>
        </Stack>
      )}

      {task?.updated_at && (
        <Stack rowGap={1} width="100%" sx={{ pr: 2, ml: 2.5 }}>
          <FieldLabel label="Updated at" />
          <Typography variant="body2" sx={{ color: grey[800] }}>
            {format(new Date(task.updated_at), "MMM dd, yyyy hh:mm aaa")}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
